import Skeleton from '@cvent/carina/components/Skeleton';
import {Card} from '../card/Card';
import {useSkeletonDelay} from './useSkeletonDelay';

interface ChangePlansSkeletonProps {
  /**
   * Skip the 300ms delay and show skeleton immediately
   * Use this for data-loading states after Suspense to prevent remount flicker
   */
  skipDelay?: boolean;
}

/**
 * Skeleton for ChangePlans page (full-screen feature comparison layout)
 * Includes a 300ms delay to prevent flash on fast-loading pages (unless skipDelay is true)
 */
export const ChangePlansSkeleton = ({
  skipDelay = false,
}: ChangePlansSkeletonProps) => {
  const show = useSkeletonDelay(skipDelay);

  if (!show) {
    return null;
  }

  return (
    <div
      className="flex flex-col items-center justify-center h-full gap-6"
      data-cvent-id="change-plans-skeleton"
    >
      {/* Optional alert skeleton */}
      <div className="pt-6 w-[90%] max-w-[1445px]">
        <Skeleton className="w-full h-[60px] rounded" hasShimmer />
      </div>

      {/* Main content card */}
      <Card styleClasses="p-8 w-[90%] max-w-[1381px]">
        {/* Header with title and billing toggle */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <Skeleton className="w-[200px] h-[28px] rounded mb-2" hasShimmer />
            <Skeleton className="w-[350px] h-[18px] rounded" hasShimmer />
          </div>
          <Skeleton className="w-[200px] h-[40px] rounded-full" hasShimmer />
        </div>

        {/* Feature comparison table skeleton */}
        <div className="mt-6">
          {/* Table header with plan columns */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Skeleton className="w-full h-[16px] rounded" hasShimmer />
            </div>
            {Array.from({length: 3}).map((_, index) => (
              <div key={index} className="w-[200px]">
                <Skeleton className="w-full h-[80px] rounded-lg" hasShimmer />
              </div>
            ))}
          </div>

          {/* Feature rows */}
          {Array.from({length: 8}).map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 mb-3">
              <div className="flex-1">
                <Skeleton className="w-[180px] h-[20px] rounded" hasShimmer />
              </div>
              {Array.from({length: 3}).map((__, colIndex) => (
                <div key={colIndex} className="w-[200px] flex justify-center">
                  <Skeleton
                    className="w-[24px] h-[24px] rounded-full"
                    hasShimmer
                  />
                </div>
              ))}
            </div>
          ))}

          {/* Footer text */}
          <div className="pt-4 mt-4 border-t">
            <Skeleton className="w-full h-[14px] rounded mb-1" hasShimmer />
            <Skeleton className="w-[80%] h-[14px] rounded" hasShimmer />
          </div>
        </div>
      </Card>
    </div>
  );
};
